import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, addDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { getAuth } from 'firebase/auth';
import { Edit2, Check, X, ArrowLeft, ArrowRight, Plus } from 'lucide-react';
import withProjectPermissions from '../components/withProjectPermissions';
import CertificateViewerModal from '../components/CertificateViewerModal';
import { DEFAULT_APP_PRIMARY } from '../utils/branding';

const tabStyle = (active, color) => ({
  padding: '6px 14px',
  borderRadius: 20,
  background: active ? color : 'var(--branding-app-surface)',
  color: active ? '#fff' : 'var(--cc-text-muted)',
  border: 'none',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
  transition: 'all 0.15s ease',
});

const ProfileModal = ({ open, onClose, imageUrl, memberName }) => {
  if (!open || !imageUrl) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.35)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: 'var(--cc-card-bg)', borderRadius: 14, minWidth: 340,
        maxWidth: '90vw', maxHeight: '90vh', boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px', borderBottom: '1px solid var(--branding-app-surface)',
        }}>
          <span style={{ fontWeight: 600, fontSize: 16, color: 'var(--cc-text-primary)' }}>
            {memberName || 'Profile picture'}
          </span>
          <button onClick={onClose} style={{
            background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)',
            border: 'none', borderRadius: 6, padding: '4px 14px',
            fontWeight: 500, cursor: 'pointer', fontSize: 15,
          }}>Close</button>
        </div>
        <div style={{
          padding: 24, overflow: 'auto', maxHeight: '70vh',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img
            src={imageUrl}
            alt={memberName || 'Profile picture'}
            style={{
              maxWidth: '90vw', maxHeight: '70vh', borderRadius: 12,
              border: '3px solid var(--branding-app-surface-panel-border)',
              objectFit: 'contain',
            }}
          />
        </div>
      </div>
    </div>
  );
};

const NewTeamModal = ({ open, onClose, onCreated, projectId }) => {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [color, setColor] = useState(DEFAULT_APP_PRIMARY);
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(''); setRole(''); setColor(DEFAULT_APP_PRIMARY); };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !role.trim()) return;
    setSaving(true);
    try {
      await addDoc(collection(db, `projects/${projectId}/teams`), {
        name: name.trim(),
        role: role.trim(),
        color,
        status: 'active',
        finishedAt: null,
        finishedBy: null,
        finishedByName: null,
        createdAt: serverTimestamp(),
      });
      reset();
      onCreated();
    } catch (err) {
      console.error('Error creating team:', err);
      alert('Failed to create team. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div style={{
        background: 'var(--cc-card-bg)', borderRadius: 16, width: 520,
        maxWidth: '92vw', boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid var(--branding-app-surface)',
        }}>
          <span style={{ fontWeight: 700, fontSize: 17, color: 'var(--cc-text-primary)' }}>Add Team</span>
          <button onClick={handleClose} style={{
            background: 'var(--cc-cancel-bg)', border: 'none', borderRadius: 6,
            padding: '4px 12px', cursor: 'pointer', color: 'var(--cc-text-muted)', fontSize: 14,
          }}>Cancel</button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'var(--cc-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Company</span>
            <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'var(--cc-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</span>
            <span style={{ width: 50 }} />
            <span style={{ width: 42 }} />
          </div>
          <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoFocus
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8,
                border: '1px solid var(--branding-app-surface-panel-border)',
                fontSize: 14, color: 'var(--cc-text-primary)',
                background: 'var(--branding-app-surface)',
                outline: 'none',
              }}
            />
            <input
              type="text"
              value={role}
              onChange={e => setRole(e.target.value)}
              required
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 8,
                border: '1px solid var(--branding-app-surface-panel-border)',
                fontSize: 14, color: 'var(--cc-text-primary)',
                background: 'var(--branding-app-surface)',
                outline: 'none',
              }}
            />
            <input
              type="color"
              value={color}
              onChange={e => setColor(e.target.value)}
              style={{ width: 50, height: 42, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2, flexShrink: 0 }}
            />
            <button
              type="submit"
              disabled={saving || !name.trim() || !role.trim()}
              style={{
                background: saving || !name.trim() || !role.trim() ? '#94a3b8' : '#22c55e',
                color: '#fff', border: 'none', borderRadius: 8,
                width: 42, height: 42, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: saving || !name.trim() || !role.trim() ? 'not-allowed' : 'pointer',
              }}
            >
              <Plus size={18} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const TeamsLanding = ({ permissions }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [teams, setTeams] = useState([]);
  const [membersByTeam, setMembersByTeam] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [certModal, setCertModal] = useState({ open: false, url: null });
  const [profileModal, setProfileModal] = useState({ open: false, url: null, name: null });
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [editTeamColor, setEditTeamColor] = useState('');
  const [movingMember, setMovingMember] = useState(null);
  const [moveLoading, setMoveLoading] = useState(false);
  const [newTeamOpen, setNewTeamOpen] = useState(false);

  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    if (!movingMember) return;
    const handle = (e) => {
      if (!e.target.closest?.('[data-move-dropdown]')) setMovingMember(null);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [movingMember]);

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      try {
        const snap = await getDoc(doc(db, 'projects', projectId));
        if (snap.exists()) setProject(snap.data());
      } catch (err) {
        console.error('Error fetching project:', err);
      }
    };
    fetchProject();
  }, [projectId]);

  const fetchTeamsAndMembers = async () => {
    if (!projectId) return;
    try {
      const teamSnap = await getDocs(collection(db, `projects/${projectId}/teams`));
      const teamsData = teamSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTeams(teamsData);

      const membersObj = {};
      for (const team of teamsData) {
        const membersSnap = await getDocs(
          collection(db, `projects/${projectId}/teams/${team.id}/memberDetails`)
        );
        const members = membersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const enriched = await Promise.all(
          members.map(async (member) => {
            try {
              const g = await getDoc(doc(db, `global_backup/users/profiles/${member.id}`));
              if (g.exists()) {
                const gd = g.data();
                return { ...member, profilePictureUrl: gd?.profilePictureUrl || null, certificationUrls: gd?.certificationUrls || [] };
              }
            } catch { /* fallback */ }
            return member;
          })
        );
        membersObj[team.id] = enriched;
      }
      setMembersByTeam(membersObj);
    } catch (err) {
      console.error('Error fetching teams or members:', err);
    }
  };

  useEffect(() => { fetchTeamsAndMembers(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeTeams = teams.filter(t => t.status !== 'finished');
  const finishedTeams = teams.filter(t => t.status === 'finished');

  const filteredTeams = (() => {
    if (!selectedTeam) return teams;
    if (selectedTeam === 'active') return activeTeams;
    if (selectedTeam === 'finished') return finishedTeams;
    return teams.filter(t => t.id === selectedTeam);
  })();

  const isProjectCreator = Boolean(permissions?.canEdit);

  const refreshTeams = async () => {
    const snap = await getDocs(collection(db, `projects/${projectId}/teams`));
    setTeams(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleMarkTeamFinished = async (teamId) => {
    if (project?.status === 'closed') { alert('This project is closed.'); return; }
    if (!isProjectCreator) { alert('Only the project manager can mark teams as finished.'); return; }
    if (!window.confirm('Mark this team as finished? This cannot be undone.')) return;
    try {
      await updateDoc(doc(db, `projects/${projectId}/teams`, teamId), {
        status: 'finished',
        finishedAt: serverTimestamp(),
        finishedBy: user.uid,
        finishedByName: user.displayName || user.email || 'Unknown User',
        finishedReason: 'Manually finished by project manager',
      });
      await refreshTeams();
      setEditingTeam(null);
    } catch {
      alert('Failed to mark team as finished. Please try again.');
    }
  };

  const handleStartEdit = (team) => {
    if (project?.status === 'closed') { alert('This project is closed.'); return; }
    if (!isProjectCreator) { alert('Only the project manager can edit teams.'); return; }
    setEditingTeam(team.id);
    setEditTeamName(team.name);
    setEditTeamColor(team.color);
  };

  const handleCancelEdit = () => {
    setEditingTeam(null);
    setEditTeamName('');
    setEditTeamColor('');
  };

  const handleSaveEdit = async () => {
    if (!isProjectCreator) { alert('Only the project manager can edit teams.'); return; }
    if (!editTeamName.trim()) { alert('Team name cannot be empty.'); return; }
    try {
      await updateDoc(doc(db, `projects/${projectId}/teams`, editingTeam), {
        name: editTeamName.trim(),
        color: editTeamColor,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
        updatedByName: user.displayName || user.email || 'Unknown User',
      });
      await refreshTeams();
      setEditingTeam(null);
      setEditTeamName('');
      setEditTeamColor('');
    } catch {
      alert('Failed to update team. Please try again.');
    }
  };

  const handleMoveMember = async (memberId, sourceTeamId, targetTeamId) => {
    if (!isProjectCreator) { alert('Only the project manager can move members between teams.'); return; }
    setMoveLoading(true);
    try {
      const sourceRef = doc(db, `projects/${projectId}/teams/${sourceTeamId}/memberDetails/${memberId}`);
      const targetRef = doc(db, `projects/${projectId}/teams/${targetTeamId}/memberDetails/${memberId}`);
      const snap = await getDoc(sourceRef);
      if (!snap.exists()) return;
      await setDoc(targetRef, snap.data());
      await deleteDoc(sourceRef);
      setMembersByTeam(prev => {
        const next = { ...prev };
        const member = (next[sourceTeamId] || []).find(m => m.id === memberId);
        next[sourceTeamId] = (next[sourceTeamId] || []).filter(m => m.id !== memberId);
        if (member) next[targetTeamId] = [...(next[targetTeamId] || []), member];
        return next;
      });
      setMovingMember(null);
    } catch (err) {
      console.error('Error moving member:', err);
      alert('Failed to move member. Please try again.');
    } finally {
      setMoveLoading(false);
    }
  };

  if (!project) return <p style={{ padding: 40 }}>Loading...</p>;

  const otherActiveTeams = (sourceTeamId) => activeTeams.filter(t => t.id !== sourceTeamId);
  const canCreateTeam = isProjectCreator && project?.status !== 'closed';

  return (
    <div style={{ padding: '32px 40px', backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
      {/* Back button */}
      <button
        onClick={() => navigate(`/project/${projectId}`)}
        style={{
          background: 'transparent', border: 'none',
          color: 'var(--cc-text-muted)', cursor: 'pointer',
          padding: '4px 0', marginBottom: 28,
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 14, fontWeight: 500,
        }}
        onMouseEnter={e => e.currentTarget.style.color = 'var(--branding-app-primary)'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--cc-text-muted)'}
      >
        <ArrowLeft size={16} />
        Back to project
      </button>

      {/* Project Information */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 24 }}>
            {project?.name || 'Loading...'}
          </h1>
          {project?.projectCode && (
            <p style={{ fontSize: 15, color: 'var(--cc-text-secondary)', marginBottom: 12 }}>
              <strong>Project Code:</strong> {project.projectCode}
            </p>
          )}
          <p style={{ fontSize: 16, color: 'var(--cc-text-secondary)', marginBottom: 16 }}>
            <strong>Address:</strong> {project?.address || 'Loading...'}
          </p>
          <p style={{ fontSize: 16, color: 'var(--cc-text-secondary)', marginBottom: 24 }}>
            <strong>Postcode:</strong> {project?.postcode || 'Loading...'}
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto' }}>

        {/* Filter tabs + New Team button */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20, alignItems: 'center' }}>
          <button style={tabStyle(!selectedTeam, 'var(--branding-app-primary)')} onClick={() => setSelectedTeam(null)}>
            All Teams
          </button>
          <button style={tabStyle(selectedTeam === 'active', '#22c55e')} onClick={() => setSelectedTeam('active')}>
            Active ({activeTeams.length})
          </button>
          <button style={tabStyle(selectedTeam === 'finished', '#6b7280')} onClick={() => setSelectedTeam('finished')}>
            Finished ({finishedTeams.length})
          </button>
          {teams.map(team => (
            <button
              key={team.id}
              style={tabStyle(selectedTeam === team.id, team.color)}
              onClick={() => setSelectedTeam(team.id)}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: team.color, flexShrink: 0,
                display: 'inline-block',
              }} />
              {team.name}
            </button>
          ))}

          {canCreateTeam && (
            <button
              onClick={() => setNewTeamOpen(true)}
              style={{
                marginLeft: 'auto',
                background: 'var(--branding-app-primary)',
                color: 'var(--branding-app-primary-fg)',
                border: 'none', borderRadius: 20,
                padding: '6px 14px', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                whiteSpace: 'nowrap',
              }}
            >
              <Plus size={14} /> New Team
            </button>
          )}
        </div>

        {/* Closed project notice */}
        {project?.status === 'closed' && (
          <div style={{
            background: '#fef3c7', border: '1px solid #f59e0b',
            borderRadius: 8, padding: '12px 16px', marginBottom: 16,
          }}>
            <p style={{ margin: 0, color: '#92400e', fontSize: 13, fontWeight: 500 }}>
              This project is closed and read-only. You can view all teams but cannot modify them.
            </p>
          </div>
        )}

        {/* Empty state */}
        {filteredTeams.length === 0 && (
          <p style={{ color: 'var(--cc-text-muted)', fontSize: 15, margin: 0 }}>
            {teams.length === 0 ? 'No teams yet.' : 'No teams match the selected filter.'}
          </p>
        )}

        {/* Team cards */}
        {filteredTeams.map(team => {
          const members = membersByTeam[team.id] || [];
          const isFinished = team.status === 'finished';
          const isEditing = editingTeam === team.id;
          const canEdit = isProjectCreator && !isFinished && project?.status !== 'closed';
          const moveTargets = otherActiveTeams(team.id);

          return (
            <div
              key={team.id}
              style={{
                background: 'var(--cc-card-bg)',
                borderRadius: 12,
                borderLeft: `4px solid ${team.color}`,
                marginBottom: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              {/* Team header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 20px',
                borderBottom: members.length > 0 ? '1px solid var(--branding-app-surface)' : 'none',
              }}>
                {isEditing ? (
                  <>
                    <input
                      type="color"
                      value={editTeamColor}
                      onChange={e => setEditTeamColor(e.target.value)}
                      style={{ width: 34, height: 34, border: 'none', borderRadius: 6, cursor: 'pointer', padding: 2, flexShrink: 0 }}
                    />
                    <input
                      type="text"
                      value={editTeamName}
                      onChange={e => setEditTeamName(e.target.value)}
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); else if (e.key === 'Escape') handleCancelEdit(); }}
                      style={{
                        flex: 1, padding: '7px 12px', borderRadius: 8,
                        border: '1px solid #cbd5e1', fontSize: 15, fontWeight: 600,
                        color: 'var(--cc-text-primary)', outline: 'none',
                        background: 'var(--cc-card-bg)',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                      <button
                        onClick={handleSaveEdit}
                        style={{
                          background: '#22c55e', color: '#fff', border: 'none',
                          borderRadius: 7, padding: '7px 13px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center',
                        }}
                        title="Save"
                      >
                        <Check size={15} />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        style={{
                          background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-muted)', border: 'none',
                          borderRadius: 7, padding: '7px 13px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center',
                        }}
                        title="Cancel"
                      >
                        <X size={15} />
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <span style={{
                      width: 12, height: 12, borderRadius: '50%',
                      background: team.color, flexShrink: 0,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, fontSize: 15, color: 'var(--cc-text-primary)' }}>
                        {team.name}
                      </span>
                      {team.role && (
                        <span style={{ fontSize: 12, color: 'var(--cc-text-muted)', marginLeft: 8 }}>
                          {team.role}
                        </span>
                      )}
                    </div>
                    <span style={{
                      fontSize: 12, fontWeight: 500, color: 'var(--cc-text-muted)',
                      background: 'var(--branding-app-surface)',
                      borderRadius: 20, padding: '2px 10px', flexShrink: 0,
                    }}>
                      {members.length} member{members.length !== 1 ? 's' : ''}
                    </span>
                    {isFinished && (
                      <span style={{
                        fontSize: 12, fontWeight: 500, color: '#6b7280',
                        background: 'var(--branding-app-surface)',
                        borderRadius: 20, padding: '2px 10px', flexShrink: 0,
                      }}>
                        Finished
                      </span>
                    )}
                    {canEdit && (
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button
                          onClick={() => handleStartEdit(team)}
                          style={{
                            background: 'var(--cc-cancel-bg)', color: 'var(--cc-text-primary)',
                            border: 'none', borderRadius: 7, padding: '6px 12px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                            fontSize: 13, fontWeight: 500,
                          }}
                        >
                          <Edit2 size={13} /> Edit
                        </button>
                        <button
                          onClick={() => handleMarkTeamFinished(team.id)}
                          style={{
                            background: 'none', border: '1px solid #fca5a5', color: '#dc2626',
                            borderRadius: 7, padding: '6px 12px',
                            fontSize: 13, fontWeight: 500, cursor: 'pointer',
                          }}
                        >
                          Mark finished
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Members */}
              {members.length === 0 ? (
                <p style={{ margin: 0, padding: '14px 20px', fontSize: 13, color: 'var(--cc-text-muted)', fontStyle: 'italic' }}>
                  No members
                </p>
              ) : (
                members.map((member, idx) => {
                  const canMove = isProjectCreator && !isFinished && project?.status !== 'closed' && moveTargets.length > 0;
                  const isMoveOpen = movingMember?.memberId === member.id && movingMember?.sourceTeamId === team.id;
                  const certs = member.certificationUrls || [];

                  return (
                    <div
                      key={member.id}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 16,
                        padding: '14px 20px',
                        borderBottom: idx < members.length - 1
                          ? '1px solid var(--branding-app-surface)'
                          : 'none',
                      }}
                    >
                      {/* Avatar */}
                      <img
                        src={member.profilePictureUrl || '/crewchainDefault.png'}
                        alt={member.name}
                        onClick={() => setProfileModal({ open: true, url: member.profilePictureUrl || '/crewchainDefault.png', name: member.name })}
                        style={{
                          width: 72, height: 72, borderRadius: '50%',
                          objectFit: 'cover', flexShrink: 0,
                          border: `3px solid ${team.color}40`,
                          cursor: 'pointer',
                        }}
                      />

                      {/* Name + contact + certs */}
                      <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
                        <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--cc-text-primary)', marginBottom: 3 }}>
                          {member.name}
                        </div>
                        {(member.email || member.phoneNumber) && (
                          <div style={{ fontSize: 12, color: 'var(--cc-text-muted)', marginBottom: certs.length > 0 ? 10 : 0 }}>
                            {[member.email, member.phoneNumber].filter(Boolean).join(' · ')}
                          </div>
                        )}
                        {certs.length > 0 && (
                          <div>
                            <div style={{
                              fontSize: 10, fontWeight: 700, color: 'var(--cc-text-muted)',
                              textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
                            }}>
                              Certifications
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                              {certs.map((url, i) => (
                                <button
                                  key={i}
                                  onClick={() => setCertModal({ open: true, url })}
                                  style={{
                                    background: 'var(--branding-app-primary)',
                                    color: 'var(--branding-app-primary-fg)',
                                    border: 'none',
                                    borderRadius: 5, padding: '3px 9px',
                                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Cert {i + 1}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Move dropdown */}
                      {canMove && (
                        <div
                          style={{ position: 'relative', flexShrink: 0, paddingTop: 4 }}
                          data-move-dropdown="true"
                        >
                          <button
                            onClick={() => setMovingMember(isMoveOpen ? null : { memberId: member.id, sourceTeamId: team.id })}
                            title="Move to another team"
                            style={{
                              background: isMoveOpen ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
                              color: isMoveOpen ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
                              border: '1px solid var(--branding-app-surface-panel-border)',
                              borderRadius: 6, padding: '5px 10px',
                              fontSize: 12, fontWeight: 500, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', gap: 4,
                            }}
                          >
                            Move <ArrowRight size={12} />
                          </button>

                          {isMoveOpen && (
                            <div style={{
                              position: 'absolute', right: 0, top: 'calc(100% + 6px)',
                              background: 'var(--cc-card-bg)',
                              border: '1px solid var(--branding-app-surface-panel-border)',
                              borderRadius: 10,
                              boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                              zIndex: 300, minWidth: 180, overflow: 'hidden',
                            }}>
                              <div style={{
                                padding: '8px 14px',
                                fontSize: 11, fontWeight: 600,
                                color: 'var(--cc-text-muted)',
                                textTransform: 'uppercase', letterSpacing: '0.06em',
                                borderBottom: '1px solid var(--branding-app-surface)',
                              }}>
                                Move to team
                              </div>
                              {moveTargets.map(t => (
                                <button
                                  key={t.id}
                                  disabled={moveLoading}
                                  onClick={() => handleMoveMember(member.id, team.id, t.id)}
                                  style={{
                                    width: '100%', textAlign: 'left', background: 'none',
                                    border: 'none', padding: '10px 14px',
                                    cursor: moveLoading ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    fontSize: 13, fontWeight: 500,
                                    color: 'var(--cc-text-primary)',
                                    opacity: moveLoading ? 0.5 : 1,
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = 'var(--branding-app-surface)'}
                                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                >
                                  <span style={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    background: t.color, flexShrink: 0,
                                  }} />
                                  {t.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>

      <NewTeamModal
        open={newTeamOpen}
        onClose={() => setNewTeamOpen(false)}
        onCreated={() => { setNewTeamOpen(false); fetchTeamsAndMembers(); }}
        projectId={projectId}
      />
      <CertificateViewerModal
        open={certModal.open}
        onClose={() => setCertModal({ open: false, url: null })}
        certificateUrl={certModal.url}
      />
      <ProfileModal
        open={profileModal.open}
        onClose={() => setProfileModal({ open: false, url: null, name: null })}
        imageUrl={profileModal.url}
        memberName={profileModal.name}
      />
    </div>
  );
};

export default withProjectPermissions(TeamsLanding);
